export interface AverySheetSpec {
  pageWidthIn: number;
  pageHeightIn: number;
  marginTopIn: number;
  marginLeftIn: number;
  labelWidthIn: number;
  labelHeightIn: number;
  rowGapIn: number;
  colGapIn: number;
  rows: number;
  cols: number;
}

export const AVERY_5160: AverySheetSpec = {
  pageWidthIn: 8.5,
  pageHeightIn: 11,
  marginTopIn: 0.5,
  marginLeftIn: 0.1875,
  labelWidthIn: 2.625,
  labelHeightIn: 1,
  rowGapIn: 0,
  colGapIn: 0.125,
  rows: 10,
  cols: 3,
};

export const AVERY_5163: AverySheetSpec = {
  pageWidthIn: 8.5,
  pageHeightIn: 11,
  marginTopIn: 0.5,
  marginLeftIn: 0.15625,
  labelWidthIn: 4,
  labelHeightIn: 2,
  rowGapIn: 0,
  colGapIn: 0.1875,
  rows: 5,
  cols: 2,
};

/**
 * One label per "page" of a small-page-size PDF. The browser print dialog
 * dispatches each page to the OS printer queue; the OS + manufacturer driver
 * handles vendor-specific protocols (Brother P-touch, Dymo, etc).
 */
export interface ThermalLabelSpec {
  widthIn: number;
  heightIn: number;
  symbology: 'code128' | 'datamatrix';
  marginIn: number;
}

// Brother QL-820 with 62mm continuous tape, cut to DK-22205 standard 29mm height.
// Effective render: 62mm × 29mm = ~2.44" × 1.14".
export const THERMAL_BROTHER_QL_62MM: ThermalLabelSpec = {
  widthIn: 62 / 25.4,
  heightIn: 29 / 25.4,
  symbology: 'code128',
  marginIn: 0.08,
};

// Dymo 30334 standard "2.25" × 1.25"" multi-purpose label.
export const THERMAL_DYMO_30334: ThermalLabelSpec = {
  widthIn: 2.25,
  heightIn: 1.25,
  symbology: 'code128',
  marginIn: 0.08,
};

// DataMatrix variants of the rectangular thermal labels. The 2D symbology packs
// the 16-char `PREFIX-VND-xxxxxxxx` payload into a small square whose modules stay
// large, so it survives phone-camera capture at arm's length where Code 128's
// narrow 1D bars fall below the detector's resolution floor. Same physical media
// as the Code 128 variants — staff just pick the "DataMatrix" format when the
// label will be scanned by a phone rather than a laser wedge. Layout is
// side-by-side (code left, text right) — see buildThermalLabelPDF.
export const THERMAL_DYMO_30334_DM: ThermalLabelSpec = {
  widthIn: 2.25,
  heightIn: 1.25,
  symbology: 'datamatrix',
  marginIn: 0.08,
};

export const THERMAL_BROTHER_QL_62MM_DM: ThermalLabelSpec = {
  widthIn: 62 / 25.4,
  heightIn: 29 / 25.4,
  symbology: 'datamatrix',
  marginIn: 0.08,
};

/*
 * 2" × 1" thermal — the size actually in use, added 2026-09-01 (vendor#1103).
 *
 * **There was no 2 × 1 format at all**, so printing 2 × 1 stock meant sending a
 * 2.25 × 1.25 page to it and letting the printer scale or crop. Everything near
 * an edge was lost, which is half of the "the name is cut off" report; the other
 * half was text wrapping into the line below it (see `fit-text.ts`).
 *
 * Same 0.08" margin as its siblings. The DataMatrix variant leaves a 66.2pt text
 * column beside a 60.5pt code — narrow enough that the 15-character reference
 * clears it by 1.4pt at 7pt type, which is why the text is fitted rather than
 * trusted to fit.
 */
export const THERMAL_2X1: ThermalLabelSpec = {
  widthIn: 2.0,
  heightIn: 1.0,
  symbology: 'code128',
  marginIn: 0.08,
};

export const THERMAL_2X1_DM: ThermalLabelSpec = {
  widthIn: 2.0,
  heightIn: 1.0,
  symbology: 'datamatrix',
  marginIn: 0.08,
};

// Round 1" label — DataMatrix-only per Spike 1.2 (Code 128 doesn't fit at 8 mil
// module width across an 0.85" usable chord; DataMatrix encodes the 12-char
// VND- payload in a ~0.4" square).
export const THERMAL_ROUND_1IN: ThermalLabelSpec = {
  widthIn: 1.0,
  heightIn: 1.0,
  symbology: 'datamatrix',
  marginIn: 0.05,
};

// Avery 5293-equivalent: 1-inch round sticker sheet, 24 labels (4 cols × 6 rows).
// The PDF uses a 1" square bounding box per label; circularity is a property of
// the physical media, not the PDF layout.
export const AVERY_5293: AverySheetSpec = {
  pageWidthIn: 8.5,
  pageHeightIn: 11,
  marginTopIn: 0.5,
  marginLeftIn: 0.75,
  labelWidthIn: 1.0,
  labelHeightIn: 1.0,
  rowGapIn: 0.8,
  colGapIn: 1.0,
  rows: 6,
  cols: 4,
};
